import {
  Accordion,
  AddIcon,
  Button,
  Flex,
  Link,
  List,
  MoreHorizontalIcon,
  Popover,
  RadioFieldGroup,
  SubtractIcon,
  Typography,
} from '@iheartjane/reefer';
import isEmpty from 'lodash-es/isEmpty';
import { useState } from 'react';

import { ConfirmDiscardWrapper } from '../confirmDiscardWrapper/ConfirmDiscardWrapper';
import { ConditionModal } from './conditionModal';
import { ConditionOptionList } from './conditionOptionList';
import type {
  ApplyToOption,
  ConditionMultiselectRangeValue,
  ConditionSection,
  ConditionSelection,
  ConditionSelectionType,
  ConditionSet,
  ConditionValue,
  MultiselectRangeConditionSection,
  RangeConditionSection,
} from './conditions.types';
import {
  ConditionSelectionProvider,
  useConditionSelection,
} from './conditionSelectionProvider';
import { ConditionSummary } from './conditionSummary';
import { ConditionTagList } from './conditionTagList';
import { ExclusionOverlapBanner } from './exclusionOverlapBanner';
import { MultiselectRangeConditions } from './multiselectRangeConditions';
import { RangeConditions } from './rangeConditions';
import {
  CONDITION_SELECTION_TYPES,
  flattenOptions,
  getSelectedCount,
  getSelectedItems,
  isMultiselectRangeValue,
  isRangeValue,
  removeSelectedValue,
} from './util/conditionSelection.util';
import { validateRange } from './util/validateRange';

import styles from './conditionsGroup.module.css';

const SELECTION_TYPE_LABELS: Record<ConditionSelectionType, string> = {
  excludes: 'Exclude',
  includes: 'Include',
};

/** # of options a section needs before we show a search field */
const SEARCH_ITEM_THRESHOLD = 8;

/**
 * A message per bounded row, keyed like the value. The table renders what it is
 * handed rather than deriving this, and an invalid row does not block saving —
 * the section's bounds are a suggestion until the deal is submitted.
 */
const toRangeErrors = (
  section: MultiselectRangeConditionSection,
  value: ConditionMultiselectRangeValue
): Record<string, string> =>
  Object.entries(value).reduce<Record<string, string>>(
    (messages, [itemValue, range]) => {
      const message = validateRange(section.range ?? {}, range, section);

      return message ? { ...messages, [itemValue]: message } : messages;
    },
    {}
  );

/**
 * Whether a range section already holds a bound, which opens its row without
 * Select being pressed. A bound of `0` counts — `$0 and up` is a real floor.
 */
const hasRangeValue = (value: ConditionValue | undefined): boolean =>
  isRangeValue(value) && (value.max !== undefined || value.min !== undefined);

export interface ConditionsGroupProps {
  /** The selected "Apply to" value. */
  applyTo: string;

  /** Options for the "Apply to" radio */
  applyToOptions: ApplyToOption[];

  /** The sections to render, the descriptor the group's `applyTo` resolved to */
  conditions: ConditionSection[];

  /** Read on mount only; a grouped accordion starts expanded unless false */
  defaultExpanded?: boolean;

  disabled?: boolean;

  /** Distinguishes accordions, rendering more than one group requires distinct values */
  id?: string;

  /** Determines UI/UX, single vs multiple conditions */
  isGrouped?: boolean;

  onApplyToChange(value: string): void;

  onChange(
    selectionType: ConditionSelectionType,
    key: string,
    value: ConditionValue
  ): void;

  /** Omit to leave "Delete" out of the group menu */
  onDelete?(): void;

  onDeselect(key: string): void;

  /** Omit to leave "Duplicate" out of the group menu */
  onDuplicate?(): void;

  /** Omit to hide the button */
  onViewMatchingProducts?(): void;

  /** Sections simplified on load, each explained by a banner */
  overlaps?: ConditionSet['overlaps'];

  /** The active selections for the group being edited, used with the provider for each group */
  selection: ConditionSelection;

  /** The group's heading — "Condition Group 1" */
  title?: string;
}

const ConditionsGroupBody = ({
  applyTo,
  applyToOptions,
  conditions,
  defaultExpanded = true,
  disabled,
  id = 'condition-group',
  isGrouped = false,
  onApplyToChange,
  onChange,
  onDelete,
  onDeselect,
  onDuplicate,
  onViewMatchingProducts,
  overlaps,
  selection,
  title,
}: ConditionsGroupProps) => {
  const {
    activeSection,
    activeValue,
    closeSection,
    draftSelection,
    isDirty,
    openSection,
    options,
    save,
    searchQuery,
    selectAll,
    selectionType,
    selectNone,
    setSearchQuery,
    setSelectionType,
    updateValue,
  } = useConditionSelection();

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  /** Range sections opened by Select, on top of those a bound already opened */
  const [expandedRangeKeys, setExpandedRangeKeys] = useState<string[]>([]);

  const isRangeOpen = (section: RangeConditionSection) =>
    expandedRangeKeys.includes(section.key) ||
    hasRangeValue(selection['includes'][section.key]);

  /** A range edits in place; every other section edits in a modal */
  const selectSection = (section: ConditionSection) =>
    section.type === 'range'
      ? setExpandedRangeKeys((keys) => [...keys, section.key])
      : openSection(section.key);

  const clearSection = (section: ConditionSection) => {
    setExpandedRangeKeys((keys) => keys.filter((key) => key !== section.key));
    onDeselect(section.key);
  };

  /**
   * Committed on every keystroke — there is no Save out here — and always as an
   * include, since a range section carries no exclude option.
   */
  const renderRangeBounds = (section: RangeConditionSection) => {
    /** A section holds one range, so a multi-item descriptor edits its first */
    const [item] = section.items;
    const committed = selection['includes'][section.key];
    const value = isRangeValue(committed) ? committed : {};

    return (
      item && (
        <RangeConditions
          allowedDecimalPlaces={section.allowedDecimalPlaces}
          disabled={disabled}
          endUnit={section.endUnit}
          error={validateRange(item, value, section)}
          fieldNamePrefix={id}
          item={item}
          startUnit={section.startUnit}
          step={section.step}
          value={value}
          onChange={(updated) => onChange('includes', section.key, updated)}
        />
      )
    );
  };

  // A section fetching its own options always searches: its first page says
  // nothing about how large the catalog behind it is
  const isSearchable = (section: ConditionSection) =>
    section.type === 'multiselect' &&
    (!!section.onSearch ||
      flattenOptions(section.items).length > SEARCH_ITEM_THRESHOLD);

  const getActiveSelectionTypes = (section: ConditionSection) =>
    section.type === 'range'
      ? []
      : CONDITION_SELECTION_TYPES.map((type) => ({
          items: getSelectedItems(selection, type, section),
          type,
        })).filter(({ items }) => items.length > 0);

  const groupMenu = (onDelete || onDuplicate) && (
    <Popover
      label={`${title} actions`}
      alignment={{ horizontal: 'right', vertical: 'bottom' }}
      target={
        <Button.Icon
          icon={<MoreHorizontalIcon />}
          ariaLabel={`${title} actions`}
        />
      }
    >
      {({ closePopover }) => (
        <Popover.Content>
          <List label={`${title} actions`}>
            <List.Item
              className={styles['option']}
              px={16}
              py={12}
              onClick={() => {
                closePopover();
                onDuplicate && onDuplicate();
              }}
            >
              <Typography
                color={onDuplicate ? undefined : 'grays-light'}
                variant="body-regular"
              >
                Duplicate
              </Typography>
            </List.Item>
            <List.Item
              className={styles['option']}
              px={16}
              py={12}
              onClick={() => {
                closePopover();
                onDelete && onDelete();
              }}
            >
              <Typography
                color={onDelete ? 'error' : 'error-light'}
                variant="body-regular"
              >
                Delete group
              </Typography>
            </List.Item>
          </List>
        </Popover.Content>
      )}
    </Popover>
  );

  const conditionGroupHeader = (
    <Flex alignItems="center" justifyContent="space-between">
      <Flex flexDirection="column" pb={16}>
        {title && <Typography variant="h3-semibold">{title}</Typography>}
        {onViewMatchingProducts && (
          <Link onClick={onViewMatchingProducts}>View matching products</Link>
        )}
      </Flex>
      {isGrouped && (
        <Flex alignItems="center" gap={8} width={disabled ? 120 : 176}>
          {!disabled && groupMenu}
          <Button
            ariaLabel={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
            label={isExpanded ? 'Collapse' : 'Expand'}
            variant="tertiary"
            startIcon={
              isExpanded ? (
                <SubtractIcon size="sm" />
              ) : (
                <AddIcon size="sm" mr="auto" />
              )
            }
            full
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </Flex>
      )}
    </Flex>
  );

  /** The draft narrowed to what the range table edits, empty until it is opened */
  const multiselectRangeValue = isMultiselectRangeValue(activeValue)
    ? activeValue
    : {};

  const conditionGroupBody = (
    <>
      <Flex gap={12} border="transparent-black-ten" borderWidth="0 0 1px 0">
        <Typography variant="body-semibold">Apply to</Typography>
        <RadioFieldGroup
          name={`apply-to-${id}`}
          disabled={disabled}
          row
          externalValue={applyTo}
          onChange={(value) => onApplyToChange(value as string)}
          options={applyToOptions.map((option) => ({
            id: `${id}-${option.value}`,
            label: option.label,
            value: option.value,
          }))}
        />
      </Flex>
      {conditions.map((section, index) => {
        const populatedSelectionTypes = getActiveSelectionTypes(section);
        const isEditingInPlace =
          section.type === 'range' && isRangeOpen(section);
        const hasSelectedItems =
          populatedSelectionTypes.length > 0 || isEditingInPlace;
        const overlap = overlaps?.[section.key];

        return (
          <Flex
            key={section.key}
            data-testid={`condition-section-${section.key}`}
            flexDirection="column"
            gap={16}
            py={20}
            border={
              index < conditions.length - 1 ? 'transparent-black-ten' : 'none'
            }
            borderWidth="0 0 1px 0"
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Typography variant="body-semibold">{section.label}</Typography>
              <Flex gap={24} alignItems="center" shrink={false}>
                {hasSelectedItems && !disabled && (
                  <Link onClick={() => clearSection(section)}>Clear all</Link>
                )}
                {!isEditingInPlace && (
                  <Button
                    disabled={disabled}
                    label="Select"
                    variant="tertiary"
                    onClick={() => selectSection(section)}
                    ariaLabel={`Select ${section.label}`}
                  />
                )}
              </Flex>
            </Flex>
            {/* Full width, so the tag list's overflow link centers on the section */}
            {populatedSelectionTypes.map(({ items, type }) => (
              <Flex key={type} flexDirection="column" gap={8}>
                {section.type !== 'multiselect-range' && (
                  <Typography>
                    {SELECTION_TYPE_LABELS[type]}d in deal
                  </Typography>
                )}
                <ConditionTagList
                  items={items}
                  disabled={disabled}
                  label={section.label}
                  onRemove={(value) =>
                    onChange(
                      type,
                      section.key,
                      removeSelectedValue(selection, type, section, value)
                    )
                  }
                />
              </Flex>
            ))}
            {section.type === 'range' &&
              isRangeOpen(section) &&
              renderRangeBounds(section)}
            {overlap && (
              <ExclusionOverlapBanner label={section.label} overlap={overlap} />
            )}
          </Flex>
        );
      })}
    </>
  );

  return (
    <>
      {isGrouped ? (
        <Accordion>
          <Accordion.Item id={id} expanded={isExpanded}>
            <Accordion.Heading as="div" disableExpansion py={16}>
              {conditionGroupHeader}
            </Accordion.Heading>
            <Accordion.Summary pb={16}>
              <ConditionSummary conditions={conditions} selection={selection} />
            </Accordion.Summary>
            <Accordion.Content>{conditionGroupBody}</Accordion.Content>
          </Accordion.Item>
        </Accordion>
      ) : (
        <>
          {conditionGroupHeader}
          {conditionGroupBody}
        </>
      )}

      <ConfirmDiscardWrapper
        variant={activeSection?.modalVariant ?? 'standard'}
        open={activeSection !== null}
        setOpen={(open) => !open && closeSection()}
        hasChanges={isDirty}
        contentLabel={
          activeSection ? `Select ${activeSection.label}` : undefined
        }
      >
        {activeSection &&
          // Checks whether renderModal exists, not what it returns — a section
          // that renders nothing still owns its modal
          (activeSection.renderModal ? (
            activeSection.renderModal()
          ) : (
            <ConditionModal
              label={activeSection.label}
              searchQuery={searchQuery}
              saveDisabled={
                activeSection.type === 'multiselect-range'
                  ? !isEmpty(
                      toRangeErrors(activeSection, multiselectRangeValue)
                    )
                  : false
              }
              selectedCount={getSelectedCount(
                draftSelection,
                selectionType,
                activeSection
              )}
              selectionType={
                activeSection.allowExcludes ? selectionType : undefined
              }
              onSave={save}
              onSearchChange={
                isSearchable(activeSection) ? setSearchQuery : undefined
              }
              // "Select all" could only ever reach what has been fetched, so
              // a section paging its own options does without it
              onSelectAll={
                activeSection.type === 'multiselect' && activeSection.onSearch
                  ? undefined
                  : selectAll
              }
              onSelectionTypeChange={
                activeSection.allowExcludes ? setSelectionType : undefined
              }
              onSelectNone={selectNone}
            >
              {activeSection.type === 'multiselect' && (
                <ConditionOptionList
                  label={activeSection.label}
                  onEndReached={activeSection.onEndReached}
                  options={options}
                />
              )}
              {activeSection.type === 'multiselect-range' && (
                <MultiselectRangeConditions
                  allowedDecimalPlaces={activeSection.allowedDecimalPlaces}
                  endUnit={activeSection.endUnit}
                  errors={toRangeErrors(activeSection, multiselectRangeValue)}
                  items={activeSection.items}
                  range={activeSection.range}
                  startUnit={activeSection.startUnit}
                  step={activeSection.step}
                  value={multiselectRangeValue}
                  onChange={updateValue}
                />
              )}
            </ConditionModal>
          ))}
      </ConfirmDiscardWrapper>
    </>
  );
};

/**
 * One condition group: an "Apply to" radio above one row per section, each row
 * opening that section's modal.
 *
 * A multi-group special renders this once per OR'd group. Each group carries its
 * own `applyTo`, `conditions` and `selection`, and gets its own editing session.
 */
export const ConditionsGroup = (props: ConditionsGroupProps) => (
  <ConditionSelectionProvider
    conditions={props.conditions}
    onChange={props.onChange}
    selection={props.selection}
  >
    <ConditionsGroupBody {...props} />
  </ConditionSelectionProvider>
);
